'use client'

import React, { useState } from 'react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { usePathname, useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { ChevronDown } from 'lucide-react'

type LOCALES = 'en' | 'es' | 'fr' | 'vi' | 'zh' | 'pseudo'

const languages = {
  vi: msg`Vietnamese`,
  en: msg`English`,
  fr: msg`French`,
  es: msg`Spanish`,
  zh: msg`Chinese`
} as const

export function Switcher() {
  const router = useRouter()
  const { i18n } = useLingui()
  const pathname = usePathname()

  const [locale, setLocale] = useState<LOCALES>(pathname?.split('/')[1] as LOCALES)

  // Enable pseudo locale for non-production environments
  // if (process.env.NEXT_PUBLIC_NODE_ENV !== 'production') {
  //   languages['pseudo'] = msg`Pseudo`
  // }

  function handleChange(newLocale: LOCALES) {
    const pathNameWithoutLocale = pathname?.split('/')?.slice(2) ?? []
    const newPath = `/${newLocale}/${pathNameWithoutLocale.join('/')}`

    setLocale(newLocale)
    router.push(newPath)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant='ghost' className='rounded-xl'>
          {i18n._(languages[locale as keyof typeof languages])}
          <ChevronDown
            className='-me-1 ms-2 opacity-60'
            size={16}
            strokeWidth={2}
            aria-hidden='true'
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {Object.keys(languages).map((lang) => (
          <DropdownMenuCheckboxItem
            key={lang}
            checked={locale === lang}
            onCheckedChange={() => handleChange(lang as LOCALES)}
          >
            {i18n._(languages[lang as keyof typeof languages])}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
